import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Calculator,
  CheckCircle2,
  Download,
  Edit3,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import { blankHirarc } from "../lib/projectStore";
import { hirarcBand, hirarcBandLabel, hirarcScore } from "../lib/hirarc";
import type {
  ActionStatus,
  ControlHierarchy,
  DesignRisk,
  EvidenceItem,
  HirarcAssessment,
} from "../types/domain";

const csvCell = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;

interface HirarcProps {
  assessments: HirarcAssessment[];
  risks: DesignRisk[];
  evidence: EvidenceItem[];
  onSave: (assessment: HirarcAssessment) => void;
  onRemove: (id: string) => void;
}

export function HirarcView({
  assessments,
  risks,
  evidence,
  onSave,
  onRemove,
}: HirarcProps) {
  const [draft, setDraft] = useState<HirarcAssessment | null>(null);
  const [filter, setFilter] = useState<"all" | "high" | "medium" | "low">("all");
  const visible = useMemo(
    () =>
      assessments.filter(
        (item) => filter === "all" || hirarcBand(item.residualRiskScore) === filter,
      ),
    [assessments, filter],
  );

  const updateDraft = (change: Partial<HirarcAssessment>) => {
    if (!draft) return;
    const next = { ...draft, ...change };
    next.riskScore = hirarcScore(next.likelihood, next.severity);
    next.residualRiskScore = hirarcScore(
      next.residualLikelihood,
      next.residualSeverity,
    );
    next.updatedAt = new Date().toISOString();
    setDraft(next);
  };

  const exportCsv = () => {
    const headings = [
      "Activity",
      "Location",
      "Hazard",
      "Consequence",
      "Persons at risk",
      "Existing controls",
      "Likelihood",
      "Severity",
      "Initial risk",
      "Recommended controls",
      "Hierarchy",
      "PIC",
      "Due date",
      "Status",
      "Residual likelihood",
      "Residual severity",
      "Residual risk",
      "Review date",
    ];
    const rows = assessments.map((item) =>
      [
        item.activity,
        item.location,
        item.hazard,
        item.consequence,
        item.personsAtRisk,
        item.existingControls,
        item.likelihood,
        item.severity,
        item.riskScore,
        item.recommendedControls,
        item.controlHierarchy,
        item.owner,
        item.dueDate,
        item.status,
        item.residualLikelihood,
        item.residualSeverity,
        item.residualRiskScore,
        item.reviewDate,
      ]
        .map(csvCell)
        .join(","),
    );
    const blob = new Blob([[headings.map(csvCell).join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = "design-safety-my-hirarc.csv";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker">
            <Calculator size={16} /> DOSH HIRARC model
          </span>
          <h2>Risk assessment that stays connected to design and evidence.</h2>
          <p>
            Score likelihood × severity, select controls using the hierarchy, assign a PIC and
            reassess the residual risk. High risk is 15–25, medium 5–12 and low 1–4.
          </p>
        </div>
        <a
          className="button button-secondary"
          href="https://dosh.gov.my/wp-content/uploads/2026/05/Guidelines-for-Hazard-Identification-Risk-Assessment-and-Risk-Control-HIRARC-2008.pdf#page=24"
          target="_blank"
          rel="noreferrer"
        >
          Official HIRARC form
        </a>
      </section>

      <section className="toolbar">
        <div className="segmented-filter" aria-label="Residual risk filter">
          {(["all", "high", "medium", "low"] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
            >
              {value}
            </button>
          ))}
        </div>
        <button className="button button-secondary" onClick={exportCsv} disabled={!assessments.length}>
          <Download size={17} /> Export CSV
        </button>
        <button className="button button-primary" onClick={() => setDraft(blankHirarc())}>
          <Plus size={18} /> Add HIRARC row
        </button>
      </section>

      {assessments.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck size={32} />}
          title="No HIRARC assessments yet"
          action={
            <button className="button button-primary" onClick={() => setDraft(blankHirarc())}>
              <Plus size={18} /> Start the assessment
            </button>
          }
        >
          Classify the activity, identify the hazard and consequence, score risk, define controls,
          assign responsibility and record residual risk.
        </EmptyState>
      ) : (
        <section className="hirarc-list">
          {visible.map((item) => (
            <article className="hirarc-card" key={item.id}>
              <div className="hirarc-card-main">
                <div className={`risk-score score-${hirarcBand(item.riskScore)}`}>
                  <strong>{item.riskScore}</strong>
                  <span>initial</span>
                </div>
                <div className="hirarc-copy">
                  <span className="eyebrow">{item.activity || "Untitled activity"}</span>
                  <h3>{item.hazard || "Hazard not described"}</h3>
                  <p>{item.consequence || "Consequence not recorded"}</p>
                  <div className="tag-row">
                    <span>{item.location || "Location not set"}</span>
                    <span>{item.controlHierarchy}</span>
                    <span>PIC: {item.owner || "Unassigned"}</span>
                  </div>
                </div>
                <div className={`residual-score score-${hirarcBand(item.residualRiskScore)}`}>
                  <small>Residual</small>
                  <strong>{item.residualRiskScore}</strong>
                  <span>{hirarcBandLabel(item.residualRiskScore)}</span>
                </div>
              </div>
              <div className="hirarc-controls">
                <div>
                  <small>Existing controls</small>
                  <p>{item.existingControls || "None recorded"}</p>
                </div>
                <div>
                  <small>Recommended controls</small>
                  <p>{item.recommendedControls || "No additional controls recorded"}</p>
                </div>
              </div>
              <footer>
                <StatusPill status={item.status === "complete" ? "success" : "warning"}>
                  {item.status}
                </StatusPill>
                <span>Due {item.dueDate || "not set"}</span>
                <span>{item.linkedRiskIds.length} linked design risk(s)</span>
                <div className="row-actions">
                  <button className="icon-button" aria-label="Edit HIRARC" onClick={() => setDraft(item)}>
                    <Edit3 size={16} />
                  </button>
                  <button
                    className="icon-button danger"
                    aria-label="Delete HIRARC"
                    onClick={() => window.confirm("Delete this HIRARC assessment?") && onRemove(item.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </footer>
            </article>
          ))}
        </section>
      )}

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">DOSH HIRARC workflow</span>
                <h2>{assessments.some((item) => item.id === draft.id) ? "Edit" : "Add"} assessment</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="modal-scroll">
              <div className="form-section-title">1. Hazard identification</div>
              <div className="form-grid">
                <Field label="Work activity" wide>
                  <input value={draft.activity} onChange={(e) => updateDraft({ activity: e.target.value })} />
                </Field>
                <Field label="Process or location">
                  <input value={draft.location} onChange={(e) => updateDraft({ location: e.target.value })} />
                </Field>
                <Field label="Persons at risk">
                  <input value={draft.personsAtRisk} onChange={(e) => updateDraft({ personsAtRisk: e.target.value })} />
                </Field>
                <Field label="Hazard" wide>
                  <textarea rows={3} value={draft.hazard} onChange={(e) => updateDraft({ hazard: e.target.value })} />
                </Field>
                <Field label="Credible cause or effect" wide>
                  <textarea rows={3} value={draft.consequence} onChange={(e) => updateDraft({ consequence: e.target.value })} />
                </Field>
                <Field label="Existing controls" wide>
                  <textarea rows={3} value={draft.existingControls} onChange={(e) => updateDraft({ existingControls: e.target.value })} />
                </Field>
              </div>

              <div className="form-section-title">2. Risk analysis</div>
              <div className="score-editor">
                <Field label="Likelihood (1–5)">
                  <input type="number" min={1} max={5} value={draft.likelihood} onChange={(e) => updateDraft({ likelihood: Number(e.target.value) })} />
                </Field>
                <span>×</span>
                <Field label="Severity (1–5)">
                  <input type="number" min={1} max={5} value={draft.severity} onChange={(e) => updateDraft({ severity: Number(e.target.value) })} />
                </Field>
                <div className={`score-result score-${hirarcBand(draft.riskScore)}`}>
                  <small>Initial risk</small>
                  <strong>{draft.riskScore}</strong>
                  <span>{hirarcBandLabel(draft.riskScore)}</span>
                </div>
              </div>

              <div className="form-section-title">3. Risk control</div>
              <div className="form-grid">
                <Field label="Recommended control measures" wide>
                  <textarea rows={3} value={draft.recommendedControls} onChange={(e) => updateDraft({ recommendedControls: e.target.value })} />
                </Field>
                <Field label="Highest control level">
                  <select value={draft.controlHierarchy} onChange={(e) => updateDraft({ controlHierarchy: e.target.value as ControlHierarchy })}>
                    <option value="elimination">Elimination</option>
                    <option value="substitution">Substitution</option>
                    <option value="engineering">Engineering</option>
                    <option value="administrative">Administrative</option>
                    <option value="ppe">PPE</option>
                  </select>
                </Field>
                <Field label="PIC / owner">
                  <input value={draft.owner} onChange={(e) => updateDraft({ owner: e.target.value })} />
                </Field>
                <Field label="Due date">
                  <input type="date" value={draft.dueDate} onChange={(e) => updateDraft({ dueDate: e.target.value })} />
                </Field>
                <Field label="Status">
                  <select value={draft.status} onChange={(e) => updateDraft({ status: e.target.value as ActionStatus })}>
                    <option value="open">Open</option>
                    <option value="in-progress">In progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="complete">Complete</option>
                  </select>
                </Field>
                <Field label="Residual likelihood">
                  <input type="number" min={1} max={5} value={draft.residualLikelihood} onChange={(e) => updateDraft({ residualLikelihood: Number(e.target.value) })} />
                </Field>
                <Field label="Residual severity">
                  <input type="number" min={1} max={5} value={draft.residualSeverity} onChange={(e) => updateDraft({ residualSeverity: Number(e.target.value) })} />
                </Field>
                <Field label="Review date">
                  <input type="date" value={draft.reviewDate} onChange={(e) => updateDraft({ reviewDate: e.target.value })} />
                </Field>
                <Field label="Review trigger">
                  <input value={draft.reviewTrigger} onChange={(e) => updateDraft({ reviewTrigger: e.target.value })} />
                </Field>
                <Field label="Linked design risks" wide>
                  <div className="check-grid">
                    {risks.map((risk) => (
                      <label key={risk.id}>
                        <input
                          type="checkbox"
                          checked={draft.linkedRiskIds.includes(risk.id)}
                          onChange={(e) =>
                            updateDraft({
                              linkedRiskIds: e.target.checked
                                ? [...draft.linkedRiskIds, risk.id]
                                : draft.linkedRiskIds.filter((id) => id !== risk.id),
                            })
                          }
                        />
                        <span>{risk.designElement || risk.hazard || "Untitled design risk"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Linked evidence" wide>
                  <div className="check-grid">
                    {evidence.map((item) => (
                      <label key={item.id}>
                        <input
                          type="checkbox"
                          checked={draft.evidenceIds.includes(item.id)}
                          onChange={(e) =>
                            updateDraft({
                              evidenceIds: e.target.checked
                                ? [...draft.evidenceIds, item.id]
                                : draft.evidenceIds.filter((id) => id !== item.id),
                            })
                          }
                        />
                        <span>{item.title || "Untitled evidence"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {draft.activity && draft.hazard && draft.owner ? (
                  <><CheckCircle2 size={17} /> Core HIRARC fields complete</>
                ) : (
                  <><AlertTriangle size={17} /> Activity, hazard and PIC are required</>
                )}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button
                className="button button-primary"
                disabled={!draft.activity.trim() || !draft.hazard.trim() || !draft.owner.trim()}
                onClick={() => {
                  onSave(draft);
                  setDraft(null);
                }}
              >
                Save assessment
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
